import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, Consent } from "./formPrimitives";
import ImageUpload from "./ImageUpload";
import ModelPicker, { OTHER_ID } from "./ModelPicker";
import { CheckCircle2, Loader2, HandCoins, Info } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = ["Som ny", "Meget god", "God", "Brukt med bruksspor", "Defekt"];
const BATTERY = ["Utmerket", "God", "Middels", "Dårlig", "Vet ikke"];
const YESNO = ["Ja", "Nei", "Delvis"];
const empty = {
  category: "", brand: "", model: "", storage: "", condition: "", battery_condition: "",
  everything_works: "", free_of_locks: "", description: "", image_id: null,
  name: "", phone: "", email: "", consent: false, company: "",
  model_pick: null, model_other: "",
};

export default function SellDevice() {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [models, setModels] = useState([]);

  useEffect(() => {
    api.get("/content/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const cat = categories.find((c) => c.name === form.category);
    if (!cat) { setModels([]); return; }
    api.get(`/content/models?category_id=${cat.id}`).then((r) => setModels(r.data)).catch(() => setModels([]));
  }, [form.category, categories]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const submit = async () => {
    const e = {};
    if (!form.category) e.category = "Velg kategori";
    if (!form.model_pick) e.model = "Velg modell";
    else if (form.model_pick.id === OTHER_ID && !form.model_other.trim()) e.model_other = "Skriv inn modellen din";
    if (!form.condition) e.condition = "Velg tilstand";
    if (!form.everything_works) e.everything_works = "Velg et alternativ";
    if (!form.free_of_locks) e.free_of_locks = "Velg et alternativ";
    if (!form.name.trim()) e.name = "Fyll inn navn";
    if (!/^[+\d][\d\s]{6,}$/.test(form.phone.trim())) e.phone = "Ugyldig telefonnummer";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Ugyldig e-postadresse";
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Sjekk feltene som er markert"); return; }
    if (!form.consent) { toast.error("Du må godta personvernvilkårene"); return; }
    setSubmitting(true);
    try {
      const { model_pick, model_other, ...payload } = form;
      await api.post("/sell-requests", payload);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Noe gikk galt. Prøv igjen.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="selg" className="py-16 sm:py-24 bg-white" data-testid="sell-section">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <div className="rounded-2xl border border-hairline bg-offwhite p-8 text-center shadow-sm" data-testid="sell-confirmation">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-9 w-9" />
            </span>
            <h3 className="mt-5 font-heading text-2xl font-bold text-navy">Takk!</h3>
            <p className="mt-3 text-ink/70">
              Vi har mottatt forespørselen din og vurderer enheten. Du får et uforpliktende
              tilbud fra MobilHub så snart som mulig.
            </p>
            <button onClick={() => { setForm(empty); setDone(false); }} className="mt-6 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-800" data-testid="sell-new-button">
              Ny forespørsel
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="selg" className="py-16 sm:py-24 bg-white" data-testid="sell-section">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div className="max-w-xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-electric">Kjøp og innbytte</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-navy">Selg enheten din til MobilHub</h2>
          <p className="mt-3 text-ink/70">
            Har du en mobil, Mac, PC eller et nettbrett du ikke bruker? Send oss informasjon om
            enheten, så vurderer vi den og gir deg et uforpliktende tilbud.
          </p>
        </div>

        <div className="mt-8 rounded-2xl border border-hairline bg-offwhite p-5 sm:p-7 shadow-sm space-y-4">
          <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} className="hidden" aria-hidden="true" data-testid="sell-honeypot" />

          <Field label="Kategori" required error={errors.category}>
            <SelectInput value={form.category} onChange={(e) => { set("category", e.target.value); setForm((f) => ({ ...f, model_pick: null, model: "", model_other: "" })); }} error={errors.category} data-testid="sell-category">
              <option value="">Velg ...</option>
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </SelectInput>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Merke"><TextInput value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder="F.eks. Apple" data-testid="sell-brand" /></Field>
            <Field label="Modell" required error={errors.model} as="div" hint={!form.category ? "Velg kategori først" : undefined}>
              <ModelPicker
                models={models}
                categoryName={form.category}
                value={form.model_pick}
                onChange={(m) => { setForm((f) => ({ ...f, model_pick: m, model: m.id === OTHER_ID ? f.model_other : m.name, brand: m.brand || f.brand })); setErrors((e) => ({ ...e, model: undefined })); }}
                otherText={form.model_other}
                onOtherText={(t) => { setForm((f) => ({ ...f, model_other: t, model: t })); setErrors((e) => ({ ...e, model_other: undefined })); }}
                otherError={errors.model_other}
                error={errors.model}
                testid="sell-model"
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Lagringskapasitet"><TextInput value={form.storage} onChange={(e) => set("storage", e.target.value)} placeholder="F.eks. 128 GB" data-testid="sell-storage" /></Field>
            <Field label="Tilstand" required error={errors.condition}>
              <SelectInput value={form.condition} onChange={(e) => set("condition", e.target.value)} error={errors.condition} data-testid="sell-condition">
                <option value="">Velg ...</option>
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </SelectInput>
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Batteritilstand">
              <SelectInput value={form.battery_condition} onChange={(e) => set("battery_condition", e.target.value)} data-testid="sell-battery">
                <option value="">Velg ...</option>
                {BATTERY.map((b) => <option key={b} value={b}>{b}</option>)}
              </SelectInput>
            </Field>
            <Field label="Fungerer alt?" required error={errors.everything_works}>
              <SelectInput value={form.everything_works} onChange={(e) => set("everything_works", e.target.value)} error={errors.everything_works} data-testid="sell-works">
                <option value="">Velg ...</option>
                {YESNO.map((y) => <option key={y} value={y}>{y}</option>)}
              </SelectInput>
            </Field>
          </div>
          <Field label="Er enheten fri for iCloud, Google-konto, PIN-koder og finansieringssperrer?" required error={errors.free_of_locks}>
            <SelectInput value={form.free_of_locks} onChange={(e) => set("free_of_locks", e.target.value)} error={errors.free_of_locks} data-testid="sell-locks">
              <option value="">Velg ...</option>
              {YESNO.map((y) => <option key={y} value={y}>{y}</option>)}
            </SelectInput>
          </Field>
          <Field label="Beskrivelse"><TextArea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Beskriv enheten, bruksspor osv." data-testid="sell-description" /></Field>
          <Field label="Bilde av enheten"><ImageUpload imageId={form.image_id} onChange={(id) => set("image_id", id)} testid="sell-image" /></Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Navn" required error={errors.name}><TextInput value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Fullt navn" error={errors.name} data-testid="sell-name" /></Field>
            <Field label="Telefonnummer" required error={errors.phone}><TextInput type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+47 ..." error={errors.phone} data-testid="sell-phone" /></Field>
          </div>
          <Field label="E-postadresse" required error={errors.email}><TextInput type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="deg@epost.no" error={errors.email} data-testid="sell-email" /></Field>

          <div className="flex items-start gap-2 rounded-lg bg-electric/5 border border-electric/20 p-3.5 text-sm text-navy">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-electric" />
            <span>Endelig pris fastsettes etter fysisk kontroll av enheten. Husk å logge ut av iCloud eller Google-konto før innlevering.</span>
          </div>

          <Consent checked={form.consent} onChange={(v) => set("consent", v)} testid="sell-consent" />

          <button onClick={submit} disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-warm px-6 py-3.5 text-base font-semibold text-navy hover:bg-warm-dark disabled:opacity-50 transition-colors" data-testid="sell-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
            Be om et tilbud
          </button>
        </div>
      </div>
    </section>
  );
}
